/**
 * The Bliksys meta-schema: a JSON Schema that describes the subset of JSON
 * Schema that Bliksys's engine can render.
 *
 * Structured as a tagged union over `type` — each variant only declares
 * the keywords that apply to its type. A string variant doesn't carry
 * `properties`/`required`/`items`; an object variant doesn't carry
 * `pattern`/`format`/`minLength`. This is what gives the schema editor a
 * focused UI: when the developer picks "string" they see only string
 * constraints.
 */

import type { JsonSchema } from '@bliksys/shared';

/**
 * Build the x-bliksys hints sub-schema for a given variant. Different
 * variants can use different widgets — e.g. `markdown` only makes sense
 * for strings, `select` only for enum-strings or arrays. Variants that
 * don't have any applicable widgets still get the rest of the hints
 * (label, placeholder, hidden, summaryField); the `widget` field is
 * omitted entirely so the schema editor doesn't show a meaningless
 * dropdown.
 *
 * `applicableWidgets`: the values that should appear in the widget
 * enum, in addition to `null`. Pass an empty array to drop the widget
 * hint entirely.
 */
function xBliksysHintsFor(applicableWidgets: readonly string[]): JsonSchema {
  const props: Record<string, JsonSchema> = {
    label: { type: 'string', title: 'Label override' },
    placeholder: { type: 'string', title: 'Placeholder text' },
  };
  if (applicableWidgets.length > 0) {
    props.widget = {
      title: 'Widget',
      enum: [null, ...applicableWidgets],
    };
  }
  props.hidden = { type: 'boolean', title: 'Hidden from editor' };
  props.summaryField = {
    type: 'string',
    title: 'Summary field',
    description: 'For object items in arrays — which field to show in list rows.',
  };
  return {
    title: 'UI hints',
    description: 'Bliksys-specific hints — affect the content editor only, not validation.',
    type: 'object',
    properties: props,
  };
}

// Widget applicability per variant:
//  - strings: text input, multi-line textarea, markdown, code highlight,
//    color picker (hex/rgba), image (URL or upload).
//  - enum-as-array (the schema's `required` field, etc.): the `select`
//    multiselect widget. Arrays in general don't get widgets.
//  - everything else: no widget hint.
const stringWidgets = ['text', 'textarea', 'markdown', 'code', 'color', 'image'];
const arrayWidgets = ['select'];

const identityFields = {
  title: {
    type: 'string',
    title: 'Title',
    description: 'Human-readable name shown in the content editor.',
  },
  description: {
    type: 'string',
    title: 'Description',
    description: 'Help text shown under the field in the content editor.',
    'x-bliksys': { widget: 'textarea' },
  },
} satisfies Record<string, JsonSchema>;

/**
 * Tail fields (default + x-bliksys hints) parametrized by:
 *  - `valueShape`: the JSON Schema describing what kind of value this
 *    variant constrains. Used so `default`/`const`/`enum.items` match
 *    the parent's type — picking type=String shouldn't let you set
 *    default to a number.
 *  - `applicableWidgets`: the values shown in the widget enum.
 *
 * Each variant calls this with its native shape so the editor's
 * suggestions are always context-correct.
 */
function tailFieldsFor(
  valueShape: JsonSchema,
  applicableWidgets: readonly string[],
): Record<string, JsonSchema> {
  return {
    default: {
      title: 'Default value',
      description: 'Used to seed new instances when the field is required.',
      // Inline so the user fills it in without zooming. The shape is the
      // variant's own — for String that's `{type:'string'}`, etc.
      'x-bliksys': { layout: 'inline' },
      ...valueShape,
    },
    'x-bliksys': xBliksysHintsFor(applicableWidgets),
  };
}

/**
 * Build an inline scalar field for the variant's own type. Used by
 * `enum`, `const`, and similar "value-shaped" tail fields.
 */
function valueShapedField(title: string, description: string, valueShape: JsonSchema): JsonSchema {
  return {
    title,
    description,
    'x-bliksys': { layout: 'inline' },
    ...valueShape,
  };
}

/**
 * Tail without `default` — for object/array variants where defaulting
 * to an empty container is implicit, and exposing a "default value"
 * widget for a complex value would be a usability disaster.
 */
function tailFieldsNoDefault(applicableWidgets: readonly string[]): Record<string, JsonSchema> {
  return {
    'x-bliksys': xBliksysHintsFor(applicableWidgets),
  };
}

function enumFieldFor(valueShape: JsonSchema): JsonSchema {
  return {
    title: 'Allowed values',
    description: 'Restricts the value to one of this list.',
    type: 'array',
    items: valueShape,
  };
}

function constFieldFor(valueShape: JsonSchema): JsonSchema {
  return valueShapedField(
    'Constant value',
    'Pin the value to exactly this. Often used as a discriminator tag.',
    valueShape,
  );
}

const stringValueShape: JsonSchema = { type: 'string' };
const stringVariant: JsonSchema = {
  title: 'String',
  description:
    "Plain text. Use for names, descriptions, identifiers, URLs, dates — anything that's a single line of text. Add a format hint or regex pattern to constrain shape.",
  'x-bliksys': {
    variantIcon: 'type',
    fieldGroups: [
      {
        id: 'identity',
        title: 'Identity',
        description: 'How this field is labeled in the editor.',
        fields: ['title', 'description', 'default'],
      },
      {
        id: 'validation',
        title: 'Validation',
        description: "Constrain the value's shape. The editor flags violations as the user types.",
        fields: ['minLength', 'maxLength', 'pattern', 'format'],
      },
      {
        id: 'choice',
        title: 'Fixed value or choice',
        description:
          '`enum` restricts the value to a list of options (renders as a select). ' +
          '`const` pins it to a single value — useful for tagged-union discriminators.',
        fields: ['enum', 'const'],
        collapsed: true,
      },
    ],
  },
  type: 'object',
  required: ['type'],
  properties: {
    type: { const: 'string' },
    ...identityFields,
    minLength: { type: 'integer', minimum: 0, title: 'Minimum length' },
    maxLength: { type: 'integer', minimum: 0, title: 'Maximum length' },
    pattern: {
      type: 'string',
      title: 'Regex pattern',
      description: 'A JavaScript-flavor regex the value must match.',
    },
    format: {
      title: 'Format',
      enum: [null, 'date', 'date-time', 'email', 'uri', 'uuid', 'hostname', 'ipv4', 'ipv6'],
    },
    enum: enumFieldFor(stringValueShape),
    const: constFieldFor(stringValueShape),
    ...tailFieldsFor(stringValueShape, stringWidgets),
  },
};

const numberValueShape: JsonSchema = { type: 'number' };
const numberVariant: JsonSchema = {
  title: 'Number',
  description:
    'A real number — integer or fractional. Use bounds and `multipleOf` to constrain the range.',
  'x-bliksys': {
    variantIcon: 'hash',
    fieldGroups: [
      {
        id: 'identity',
        title: 'Identity',
        description: 'How this field is labeled in the editor.',
        fields: ['title', 'description', 'default'],
      },
      {
        id: 'range',
        title: 'Range',
        description:
          'Constrain the value to a numeric range. Exclusive bounds reject the boundary; inclusive bounds accept it.',
        fields: ['minimum', 'maximum', 'exclusiveMinimum', 'exclusiveMaximum', 'multipleOf'],
      },
      {
        id: 'choice',
        title: 'Fixed value or choice',
        description: '`enum` restricts to a list; `const` pins to one value.',
        fields: ['enum', 'const'],
        collapsed: true,
      },
    ],
  },
  type: 'object',
  required: ['type'],
  properties: {
    type: { const: 'number' },
    ...identityFields,
    minimum: { type: 'number', title: 'Minimum' },
    maximum: { type: 'number', title: 'Maximum' },
    exclusiveMinimum: { type: 'number', title: 'Exclusive minimum' },
    exclusiveMaximum: { type: 'number', title: 'Exclusive maximum' },
    multipleOf: {
      type: 'number',
      exclusiveMinimum: 0,
      title: 'Multiple of',
    },
    enum: enumFieldFor(numberValueShape),
    const: constFieldFor(numberValueShape),
    ...tailFieldsFor(numberValueShape, []),
  },
};

const integerValueShape: JsonSchema = { type: 'integer' };
const integerVariant: JsonSchema = {
  ...numberVariant,
  title: 'Integer',
  description: 'A whole number. Like Number but rejects fractional values like 1.5.',
  'x-bliksys': {
    ...(numberVariant['x-bliksys'] ?? {}),
    variantIcon: 'binary',
  },
  properties: {
    ...numberVariant.properties,
    type: { const: 'integer' },
    enum: enumFieldFor(integerValueShape),
    const: constFieldFor(integerValueShape),
    ...tailFieldsFor(integerValueShape, []),
  },
};

const booleanValueShape: JsonSchema = { type: 'boolean' };
const booleanVariant: JsonSchema = {
  title: 'Boolean',
  description: 'true or false. Renders as a toggle in the content editor.',
  'x-bliksys': { variantIcon: 'toggle-left' },
  type: 'object',
  required: ['type'],
  properties: {
    type: { const: 'boolean' },
    ...identityFields,
    enum: enumFieldFor(booleanValueShape),
    const: constFieldFor(booleanValueShape),
    ...tailFieldsFor(booleanValueShape, []),
  },
};

const nullValueShape: JsonSchema = { type: 'null' };
const nullVariant: JsonSchema = {
  title: 'Null',
  description:
    'The literal `null` value. Rarely used standalone — usually combined with another type to express "this field can be unset".',
  'x-bliksys': { variantIcon: 'circle-slash' },
  type: 'object',
  required: ['type'],
  properties: {
    type: { const: 'null' },
    ...identityFields,
    ...tailFieldsFor(nullValueShape, []),
  },
};

const objectVariant: JsonSchema = {
  title: 'Object',
  description:
    'A structured value with named fields. The most common shape — use this for records, settings, content blocks.',
  'x-bliksys': {
    variantIcon: 'braces',
    fieldGroups: [
      {
        id: 'identity',
        title: 'Identity',
        description: 'How this schema is labeled in the editor.',
        fields: ['title', 'description', 'default'],
      },
      {
        id: 'structure',
        title: 'Structure',
        description: 'The fields this object contains and which are mandatory.',
        fields: ['properties', 'required', 'additionalProperties'],
      },
      {
        id: 'composition',
        title: 'Composition',
        description:
          'Combine multiple schemas. `oneOf` is a tagged union (pick exactly one); ' +
          '`not` forbids matching a shape. JSON Schema also defines `anyOf` and `allOf` ' +
          'which Bliksys reads but does not yet expose for authoring.',
        fields: ['not'],
        collapsed: true,
      },
      {
        id: 'conditional',
        title: 'Conditional',
        description:
          "Apply different rules depending on the value's shape. `if` is run silently " +
          'against the value; if it matches, `then` applies, otherwise `else` applies. ' +
          'Use this for rules like "when type is X, name is required".',
        fields: ['if', 'then', 'else'],
        collapsed: true,
      },
      {
        id: 'reuse',
        title: 'Reuse',
        description:
          'Named sub-schemas (`$defs`) that other places in this schema can reference ' +
          'via `$ref`. Useful when the same shape appears in multiple places — define ' +
          'it once, reference everywhere.',
        fields: ['$defs'],
        collapsed: true,
      },
    ],
  },
  type: 'object',
  required: ['type'],
  properties: {
    type: { const: 'object' },
    ...identityFields,
    properties: {
      title: 'Properties',
      description: 'Named fields and their schemas.',
      type: 'object',
      additionalProperties: { $ref: '#/$defs/Schema' },
      'x-bliksys': { layout: 'inline' },
    },
    required: {
      title: 'Required fields',
      description: 'Names of properties that must be present.',
      type: 'array',
      items: { type: 'string' },
      uniqueItems: true,
      'x-bliksys': {
        widget: 'select',
        optionsFromSibling: '../properties',
      },
    },
    additionalProperties: {
      title: 'Additional properties',
      description:
        'How the schema treats keys not listed above. Allow lets them ' +
        'through silently. Reject fails validation on any extra key. ' +
        'Constrain validates extras against a schema.',
      'x-bliksys': { discriminatorLocked: false, layout: 'inline' },
      oneOf: [
        {
          title: 'Allow',
          type: 'boolean',
          enum: [true],
          description: 'Permit any additional keys without checking them.',
        },
        {
          title: 'Reject',
          type: 'boolean',
          enum: [false],
          description: 'Reject any property not listed above (strict mode).',
        },
        {
          title: 'Constrain to schema',
          $ref: '#/$defs/Schema',
          description: 'Validate extra keys against this schema.',
        },
      ],
    },
    $defs: {
      title: 'Definitions',
      description:
        'Named sub-schemas that can be reused via $ref. Useful when the ' +
        'same shape (e.g. a Block, a Page) appears in multiple places, ' +
        'or when a oneOf needs labeled variants. Reference them as ' +
        '"#/$defs/Name" anywhere in this schema.',
      type: 'object',
      additionalProperties: { $ref: '#/$defs/Schema' },
      'x-bliksys': { layout: 'inline' },
    },
    if: {
      title: 'If',
      description:
        'Conditional rule: when the value matches this schema, the ' +
        '"Then" rules apply. Otherwise the "Else" rules apply. Common ' +
        'pattern: pin a discriminator-like field via const.',
      $ref: '#/$defs/Schema',
    },
    then: {
      title: 'Then',
      description: 'Rules that apply when "If" matches.',
      $ref: '#/$defs/Schema',
    },
    else: {
      title: 'Else',
      description: 'Rules that apply when "If" does not match.',
      $ref: '#/$defs/Schema',
    },
    not: {
      title: 'Not',
      description:
        'Forbid values matching this schema. Inverse of the surrounding ' +
        "schema's rules. Use sparingly — `oneOf` or `if` is usually clearer.",
      $ref: '#/$defs/Schema',
    },
    ...tailFieldsNoDefault([]),
  },
};

const arrayVariant: JsonSchema = {
  title: 'Array',
  description:
    'An ordered list of values, all of the same shape. Use bounds to require a minimum or cap the length, `uniqueItems` to forbid duplicates, or `contains` to require at least one matching item.',
  'x-bliksys': {
    variantIcon: 'list',
    fieldGroups: [
      {
        id: 'identity',
        title: 'Identity',
        description: 'How this list is labeled in the editor.',
        fields: ['title', 'description', 'default'],
      },
      {
        id: 'items',
        title: 'Items',
        description: 'The shape of each element. Authored inline below.',
        fields: ['items'],
      },
      {
        id: 'size',
        title: 'Size & uniqueness',
        description:
          'Constrain how many items the list contains and whether duplicates are allowed.',
        fields: ['minItems', 'maxItems', 'uniqueItems'],
      },
      {
        id: 'contains',
        title: 'Required content',
        description:
          'Require at least one item to match a sub-schema. `minContains`/`maxContains` ' +
          'turn this into "exactly N matching items".',
        fields: ['contains', 'minContains', 'maxContains'],
        collapsed: true,
      },
    ],
  },
  type: 'object',
  required: ['type'],
  properties: {
    type: { const: 'array' },
    ...identityFields,
    items: {
      title: 'Item type',
      description: 'The type of each element in this array.',
      $ref: '#/$defs/Schema',
      'x-bliksys': { layout: 'inline' },
    },
    minItems: { type: 'integer', minimum: 0, title: 'Minimum items' },
    maxItems: { type: 'integer', minimum: 0, title: 'Maximum items' },
    uniqueItems: { type: 'boolean', title: 'Unique items only' },
    contains: {
      title: 'Must contain',
      description:
        'A schema that at least one item must match. Use minContains/maxContains ' +
        'below to require multiple matches or cap them.',
      $ref: '#/$defs/Schema',
    },
    minContains: {
      type: 'integer',
      minimum: 0,
      title: 'Minimum matching items',
      description: 'How many items must match the "Must contain" schema. Defaults to 1.',
    },
    maxContains: {
      type: 'integer',
      minimum: 0,
      title: 'Maximum matching items',
      description: 'Caps the number of items that match the "Must contain" schema.',
    },
    ...tailFieldsNoDefault(arrayWidgets),
  },
};

const refVariant: JsonSchema = {
  title: 'Reference',
  description:
    'Reuse a sub-schema defined elsewhere. Local refs (`#/$defs/Name`) point inside this schema; remote refs (`https://…`) point to other schemas. Bliksys autocompletes local definitions.',
  'x-bliksys': { variantIcon: 'link' },
  type: 'object',
  required: ['$ref'],
  properties: {
    $ref: {
      type: 'string',
      title: 'Reference',
      description: 'Local pointer to a definition (e.g. "#/$defs/Block").',
      pattern: '^#(/[^/]+)+$',
      'x-bliksys': { refsFrom: '/$defs' },
    },
    title: identityFields.title,
    description: identityFields.description,
  },
};

const unionVariant: JsonSchema = {
  title: 'Union (oneOf)',
  description:
    'A tagged union. The value matches when it matches exactly one of the listed variants. A discriminator property (e.g. `type: "page"` vs `type: "post"`) lets the editor pick the right variant from the value\'s shape.',
  'x-bliksys': { variantIcon: 'split' },
  type: 'object',
  required: ['oneOf'],
  properties: {
    title: identityFields.title,
    description: identityFields.description,
    oneOf: {
      title: 'Variants',
      description: 'A value matches when it matches exactly one variant.',
      type: 'array',
      items: { $ref: '#/$defs/Schema' },
      minItems: 2,
    },
    discriminator: {
      title: 'Discriminator',
      description:
        'Names a property whose constant value identifies the active variant. ' +
        'Bliksys infers one when undeclared; setting it explicitly avoids ambiguity.',
      type: 'object',
      required: ['propertyName'],
      properties: {
        propertyName: {
          type: 'string',
          title: 'Property name',
          description:
            'A property each variant pins to a `const` value (e.g. `type` when ' +
            'every variant declares `type: { const: "..." }`). The editor ' +
            'suggests names that match across all variants.',
          'x-bliksys': { discriminatorCandidatesFrom: '../../oneOf' },
        },
      },
    },
  },
};

export const blicksMetaSchema: JsonSchema = {
  $id: 'https://bliksys.dev/meta-schema/v1',
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  title: 'JSON Schema',
  description:
    'Describes the shape of content. The Bliksys editor renders this with the ' +
    'same engine that renders content schemas, so authoring a schema feels ' +
    'like editing structured content.',
  $ref: '#/$defs/Schema',
  $defs: {
    Schema: {
      title: 'Schema',
      'x-bliksys': {
        summaryField: 'title',
        discriminatorLocked: true,
        // The schema picker IS a meaningful UI in its own right —
        // "what kind of value is this?" is the most important decision
        // an author makes about a field. Render variants as tappable
        // tiles with description + icon instead of a select dropdown.
        variantPickerLayout: 'grid',
      },
      oneOf: [
        { $ref: '#/$defs/StringSchema' },
        { $ref: '#/$defs/NumberSchema' },
        { $ref: '#/$defs/IntegerSchema' },
        { $ref: '#/$defs/BooleanSchema' },
        { $ref: '#/$defs/NullSchema' },
        { $ref: '#/$defs/ObjectSchema' },
        { $ref: '#/$defs/ArraySchema' },
        { $ref: '#/$defs/UnionSchema' },
        { $ref: '#/$defs/RefSchema' },
      ],
      // Discriminator is `type` for the type-tagged variants. UnionSchema
      // and RefSchema have no `type` so first-match selection (no
      // discriminator value present in data) picks based on which schema
      // shape the data fits.
      discriminator: { propertyName: 'type' },
    },
    StringSchema: stringVariant,
    NumberSchema: numberVariant,
    IntegerSchema: integerVariant,
    BooleanSchema: booleanVariant,
    NullSchema: nullVariant,
    ObjectSchema: objectVariant,
    ArraySchema: arrayVariant,
    UnionSchema: unionVariant,
    RefSchema: refVariant,
  },
};
